import { ApiClientError, api, auth } from "@lumberset/api-client";
import {
  HelpLauncher,
  ReportProblemButton,
  ReportProblemDialog,
  useReportProblem,
} from "@lumberset/support-ui";
import { initials } from "@lumberset/takeoff-ui";
import {
  AppShell,
  Avatar,
  Badge,
  Banner,
  Button,
  DropdownMenu,
  EmptyState,
  LogoMark,
  Sidebar,
} from "@lumberset/ui";
import {
  AcademicCap,
  ArrowRightOnRectangle,
  BookOpen,
  Bug,
  Buildings,
  ChartActivity,
  CurrencyDollar,
  EllipsisHorizontal,
  Eye,
  Lifebuoy,
  ListCheckbox,
  User,
} from "@medusajs/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useState } from "react";
import { meQuery, roleLabels, supportStatsQuery } from "../lib/queries";

/**
 * Everything behind sign-in. A session alone is not enough: the API must also
 * recognise the person as Staff, so a Yard Member never sees this shell.
 */
export const Route = createFileRoute("/_app")({
  beforeLoad: async ({ context, location }) => {
    const { data: session } = await auth.getSession();
    if (!session) throw redirect({ to: "/login", search: { redirect: location.href } });

    try {
      const { user, impersonation } = await context.queryClient.ensureQueryData(meQuery);
      return { user, impersonation };
    } catch (error) {
      if (error instanceof ApiClientError && (error.status === 401 || error.status === 403)) {
        throw redirect({ to: "/login", search: { redirect: location.href } });
      }
      throw error;
    }
  },
  component: AppLayout,
  errorComponent: ({ error, reset }) => (
    <div className="flex min-h-dvh items-center justify-center">
      <EmptyState
        title="Something went wrong"
        description={error instanceof Error ? error.message : "Please try again."}
        action={
          <Button variant="secondary" onClick={reset}>
            Try again
          </Button>
        }
      />
    </div>
  ),
});

/** Signs out, forgets everything cached, and lands on the sign-in screen. */
function useSignOut() {
  const router = useRouter();
  const queryClient = useQueryClient();
  return async () => {
    await auth.signOut();
    queryClient.clear();
    await router.invalidate();
    router.history.push("/login");
  };
}

/**
 * Across the top while an Admin is signed in as this Staff member, so it is
 * never mistaken for their own session. Stop signs out of the borrowed
 * session; the Admin's own panel is untouched, it lives on another host.
 */
function SignedInAsBanner({ name, adminName }: { name: string; adminName: string }) {
  const signOut = useSignOut();
  const [stopping, setStopping] = useState(false);
  return (
    <Banner
      icon={<Eye />}
      action={
        <Button
          size="small"
          variant="secondary"
          isLoading={stopping}
          onClick={async () => {
            setStopping(true);
            await signOut();
          }}
        >
          Stop
        </Button>
      }
    >
      Signed in as {name} by Admin {adminName}.{" "}
      <span className="max-sm:hidden">Everything done here is logged under the Admin's name.</span>
    </Banner>
  );
}

function AppLayout() {
  const { user, impersonation } = Route.useRouteContext();
  const navigate = useNavigate();
  const report = useReportProblem();
  // The count on the Support entry: open Tickets that still need someone.
  const { data: stats } = useQuery({ ...supportStatsQuery, refetchInterval: 60_000 });

  return (
    <>
      <AppShell
        banner={
          impersonation ? (
            <SignedInAsBanner name={user.name} adminName={impersonation.adminName} />
          ) : null
        }
        sidebar={
          <>
            <Sidebar.Header>
              <div className="flex items-center gap-x-2.5 p-1">
                <LogoMark className="size-7" />
                <span className="min-w-0">
                  <span className="txt-compact-small-plus block truncate text-ui-fg-base">
                    LumberSet
                  </span>
                  <span className="txt-compact-xsmall block text-ui-fg-muted">Staff panel</span>
                </span>
              </div>
            </Sidebar.Header>

            <Sidebar.Body>
              <Sidebar.Label>Today</Sidebar.Label>
              <Sidebar.Item asChild>
                <Link to="/" activeOptions={{ exact: true }}>
                  <ListCheckbox /> Review queue
                </Link>
              </Sidebar.Item>
              <Sidebar.Item asChild>
                <Link to="/support">
                  <Lifebuoy />
                  <span className="flex-1">Support</span>
                  {stats && stats.open > 0 ? (
                    <Badge color="blue" rounded="full">
                      {stats.open}
                    </Badge>
                  ) : null}
                </Link>
              </Sidebar.Item>
              <Sidebar.Item asChild>
                <Link to="/my-work">
                  <User /> My work
                </Link>
              </Sidebar.Item>
              <Sidebar.Label>People</Sidebar.Label>
              <Sidebar.Item asChild>
                <Link to="/yards">
                  <Buildings /> Workspaces
                </Link>
              </Sidebar.Item>
              <Sidebar.Label>Knowledge</Sidebar.Label>
              <Sidebar.Item asChild>
                <Link to="/prices">
                  <CurrencyDollar /> Reference prices
                </Link>
              </Sidebar.Item>
              <Sidebar.Item asChild>
                <Link to="/help/articles">
                  <BookOpen /> Help articles
                </Link>
              </Sidebar.Item>
              <Sidebar.Item asChild>
                <Link to="/accuracy">
                  <ChartActivity /> Accuracy
                </Link>
              </Sidebar.Item>
              <Sidebar.Item asChild>
                <Link to="/lessons">
                  <AcademicCap /> Reading lessons
                </Link>
              </Sidebar.Item>
            </Sidebar.Body>

            <Sidebar.Footer>
              <ReportProblemButton onClick={report.start} isLoading={report.capturing} />
              <UserMenu
                name={user.name}
                email={user.email}
                role={user.role}
                onReport={report.start}
              />
            </Sidebar.Footer>
          </>
        }
        breadcrumbs={<div id="breadcrumbs" />}
      >
        <Outlet />
      </AppShell>

      <ReportProblemDialog
        client={api}
        {...report.dialog}
        source="staff"
        onViewTicket={(id) => navigate({ to: "/support/tickets/$id", params: { id } })}
        onViewTickets={() => navigate({ to: "/support/mine" })}
      />
      <HelpLauncher api={api} onOpenTicket={report.start} />
    </>
  );
}

function UserMenu({
  name,
  email,
  role,
  onReport,
}: {
  name: string;
  email: string;
  role: string | null | undefined;
  onReport: () => void;
}) {
  const signOut = useSignOut();

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger className="transition-fg flex w-full items-center gap-x-2.5 rounded-md p-1 text-left outline-none hover:bg-ui-bg-subtle-hover focus-visible:shadow-borders-focus data-[state=open]:bg-ui-bg-subtle-hover">
        <Avatar fallback={initials(name)} size="small" shape="circle" />
        <span className="min-w-0 flex-1">
          <span className="txt-compact-small-plus block truncate text-ui-fg-base">{name}</span>
          <span className="txt-compact-xsmall block text-ui-fg-muted">
            {(role && roleLabels[role]) ?? role ?? "Staff"}
          </span>
        </span>
        <EllipsisHorizontal className="shrink-0 text-ui-fg-muted" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content side="top" className="w-[236px]">
        <DropdownMenu.Label className="truncate">{email}</DropdownMenu.Label>
        <DropdownMenu.Separator />
        <DropdownMenu.Item onSelect={onReport}>
          <Bug /> Report a problem
        </DropdownMenu.Item>
        <DropdownMenu.Separator />
        <DropdownMenu.Item onSelect={signOut}>
          <ArrowRightOnRectangle /> Sign out
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
}
